import React from 'react';
import { Plan, SystemType } from '@redpanda/types';
import { useTranslation } from 'react-i18next';

interface PricingTableProps {
  system: SystemType;
  plans: Plan[];
  onChoosePlan: (plan: Plan) => void;
  currentSubscription?: any;
  /** 沒登入的訪客：沒有「目前方案」，每個方案都是「選擇方案」（按了先去登入） */
  isGuest?: boolean;
}

const PricingTable: React.FC<PricingTableProps> = ({ system, plans, onChoosePlan, currentSubscription, isGuest = false }) => {
  const { t } = useTranslation();

  // 父層已處理載入狀態，此處只處理無資料的邊緣情況
  if (!plans || plans.length === 0) {
    return null;
  }

  // Booking/Queue 面向店家現場決策客群，刻意只顯示兩層（免費 + 付費）降低選擇成本；
  // 專業版仍保留在資料庫中（見 seed.ts），未來若要開放團隊/多店高階方案可直接顯示，不用重建資料。
  const twoTierSystems: SystemType[] = ['BOOKING', 'QUEUE'];
  const staticPlans = twoTierSystems.includes(system)
    ? [
        { id: 'free', type: 'free' },
        { id: 'basic', type: 'basic' }
      ]
    : [
        { id: 'free', type: 'free' },
        { id: 'basic', type: 'basic' },
        { id: 'pro', type: 'pro' }
      ];

  // 方案 id 固定是 <系統>_<free|basic|pro>；資料庫沒有的方案不顯示（原本會編一個 0 元的假方案，選了後端一定報錯）
  const findPlan = (planType: string) => plans.find(plan => plan.id === `${system.toLowerCase()}_${planType}`);
  const shownPlans = staticPlans.filter(p => findPlan(p.type));

  // 針對兩級定價系統，不顯示「最受歡迎」標籤
  const showPopularBadge = !twoTierSystems.includes(system);

  return (
    <div className="container mx-auto">
      {/* 月付／年付切換已暫時移除：後端尚未支援年付計費（2026-10-05），實作需求見 docs/金流.md「年付（計費週期）」 */}

      <div className={`grid grid-cols-1 gap-6 mb-16 ${shownPlans.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-3'}`}>
        {shownPlans.map((staticPlan) => {
          const planType = staticPlan.type;
          const isPopular = showPopularBadge && planType === 'basic';
          const backendPlan = findPlan(planType)!;
          // 主要按鈕：有「最受歡迎」標記時給該方案；沒有（例：只有免費／付費兩方案）時給比目前方案貴的方案
          const currentPrice = currentSubscription ? currentSubscription.plan.price : 0;
          const isUpgrade = backendPlan.price > currentPrice;
          const isPrimaryAction = showPopularBadge ? isPopular : isUpgrade;

          return (
            <div
              key={staticPlan.id}
              className={`rounded-lg p-7 text-center flex flex-col relative bg-[var(--surface)] transition-transform duration-150 ${isPopular ? 'md:scale-105' : ''}`}
              style={{
                boxShadow: isPopular ? 'var(--card-shadow-hover)' : 'var(--card-shadow)',
                outline: isPopular ? '2px solid var(--accent)' : 'none',
                outlineOffset: isPopular ? '-2px' : '0',
              }}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[var(--accent-fill)] text-[var(--accent-on)] px-3 py-1 rounded-full text-[13px] font-semibold tracking-wide">
                    {t('pricing.most_popular', { ns: 'staticContent' })}
                  </span>
                </div>
              )}

              <div className="flex-grow">
                <h2 className="text-xl font-bold mb-2 text-[var(--text-primary)]">
                  {t(`systems.${system.toLowerCase()}.plans.${planType}.name`, { ns: 'staticContent' })}
                </h2>
                <p className="text-[var(--text-muted)] mb-4 text-sm">
                  {t(`systems.${system.toLowerCase()}.plans.${planType}.description`, { ns: 'staticContent' })}
                </p>

                <div className="mb-6">
                  {/* 幣別符號小一級、數字千分位＋等寬（§3-2） */}
                  <span className="text-lg font-semibold text-[var(--text-secondary)] mr-0.5">
                    {t('pricing.currency', { ns: 'staticContent' })}
                  </span>
                  <span className="text-4xl font-bold tabular-nums text-[var(--text-primary)]">
                    {backendPlan.price.toLocaleString()}
                  </span>
                  <span className="text-base font-normal text-[var(--text-muted)] ml-1">
                    {backendPlan.price > 0 ? `/${t('pricing.per_month', { ns: 'staticContent' })}` : ''}
                  </span>
                </div>

                <ul className="text-left mb-6 space-y-3">
                  {(() => {
                    const features = t(`systems.${system.toLowerCase()}.plan_features.${planType}`, { ns: 'staticContent', returnObjects: true });
                    const featureArray = Array.isArray(features) ? features : [];

                    return featureArray.length > 0 ? (
                      featureArray.map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <svg className="w-4 h-4 mr-3 text-[var(--accent)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          <span className="text-sm text-[var(--text-secondary)]">{feature}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-[var(--text-muted)] text-sm italic">
                        {t('pricing.no_features', { ns: 'translation', defaultValue: '方案功能載入中...' })}
                      </li>
                    );
                  })()}
                </ul>
              </div>

              {/* 檢查是否為當前方案：有訂閱記錄時比對 plan.id；無記錄時免費方案即為當前方案（訪客沒有目前方案） */}
              {!isGuest && (currentSubscription ? currentSubscription.plan.id === backendPlan.id : planType === 'free') ? (
                <button
                  disabled
                  className="mt-auto font-semibold py-2.5 px-6 rounded-lg bg-[var(--accent-soft)] text-[var(--accent-soft-text)] text-sm cursor-default"
                >
                  {t('pricing.current_plan', { ns: 'staticContent' })}
                </button>
              ) : (
                <button
                  onClick={() => onChoosePlan(backendPlan)}
                  className={`mt-auto font-semibold py-2.5 px-6 rounded-lg text-sm transition-opacity duration-150 hover:opacity-90 ${
                    isPrimaryAction
                      ? 'bg-[var(--accent-fill)] text-[var(--accent-on)]'
                      : 'border border-[var(--border-c)] text-[var(--text-primary)] hover:bg-[var(--page-bg)]'
                  }`}
                >
                  {!currentSubscription
                    ? t(`systems.${system.toLowerCase()}.choose_plan`, { ns: 'staticContent' })
                    : isUpgrade
                      ? t('subscription.upgrade', { ns: 'translation' })
                      : t('pages.subscription.change_plan', { ns: 'translation' })}
                </button>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default PricingTable;
